import React from 'react';
import { Card } from 'antd';
import { ClockCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';

const isCreditInvoice = (invoice) => {
  if (invoice?.isCreditInvoice || invoice?.documentType === "credit") return true;
  if (Number(invoice?.total) < 0) return true;
  if (Number(invoice?.remaining) < 0) return true;
  if (Array.isArray(invoice?.items) && invoice.items.some((item) => Number(item?.qty) < 0 || Number(item?.price) < 0)) {
    return true;
  }
  return false;
};

/** Credit invoices contribute negative amounts to summary totals */
const getSignedAmount = (invoice, amount) => {
  const numeric = Number(amount || 0);
  if (Number.isNaN(numeric)) return 0;
  if (isCreditInvoice(invoice)) return -Math.abs(numeric);
  return numeric;
};

const CardsContainer = ({ invoicesData }) => {
  const supportedCurrencies = ["USD", "EUR"];

  const normalizeCurrency = (currency) => {
    const normalized = String(currency || "USD").toUpperCase();
    return supportedCurrencies.includes(normalized) ? normalized : "USD";
  };

  const calculateTotals = () => {
    const initialTotals = supportedCurrencies.reduce((acc, currency) => {
      acc[currency] = {
        totalAmount: 0,
        remainingAmount: 0,
        overdueAmount: 0,
      };
      return acc;
    }, {});

    if (!invoicesData || invoicesData.length === 0) {
      return initialTotals;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return invoicesData.reduce((acc, invoice) => {
      const currency = normalizeCurrency(invoice.currency);
      const total = getSignedAmount(invoice, invoice.total);
      const remaining = getSignedAmount(invoice, invoice.remaining);
      const dueDate = invoice.dueDate ? new Date(invoice.dueDate) : null;
      const hasOutstanding = Math.abs(remaining) > 0;
      const isOverdue =
        dueDate &&
        !Number.isNaN(dueDate.getTime()) &&
        dueDate.setHours(0, 0, 0, 0) < today.getTime() &&
        hasOutstanding;

      acc[currency].totalAmount += total;
      acc[currency].remainingAmount += remaining;
      acc[currency].overdueAmount += isOverdue ? remaining : 0;
      return acc;
    }, initialTotals);
  };

  const totals = calculateTotals();

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatPercent = (amount, total) => {
    if (!total) return "0.00";
    return ((amount / total) * 100).toFixed(2);
  };

  const CurrencyBreakdown = ({ type, className }) => (
    <div className="space-y-1">
      {supportedCurrencies.map((currency) => (
        <div key={currency} className={`text-2xl font-bold ${className}`}>
          {formatCurrency(totals[currency][type], currency)}
        </div>
      ))}
    </div>
  );

  const CurrencyPercentBreakdown = ({ type }) => (
    <div className="text-sm text-gray-500 mt-1 space-y-1">
      {supportedCurrencies.map((currency) => (
        <p key={currency} className="m-0">
          {currency}: {formatPercent(totals[currency][type], totals[currency].totalAmount)}% of total
        </p>
      ))}
    </div>
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
      <Card 
        className="shadow-sm hover:shadow-md transition-shadow" 
        bordered={false}
        headStyle={{ borderBottom: 0 }}
        bodyStyle={{ padding: '24px' }}
      >
        <div className="flex items-center mb-3">
          <div className="rounded-full bg-amber-100 p-2 mr-3">
            <ClockCircleOutlined className="text-amber-600 text-xl" />
          </div>
          <span className="text-lg font-medium text-gray-700">Outstanding Amount</span>
        </div>
        <CurrencyBreakdown type="remainingAmount" className="text-amber-600" />
        <CurrencyPercentBreakdown type="remainingAmount" />
      </Card>

      <Card 
        className="shadow-sm hover:shadow-md transition-shadow" 
        bordered={false}
        headStyle={{ borderBottom: 0 }}
        bodyStyle={{ padding: '24px' }}
      >
        <div className="flex items-center mb-3">
          <div className="rounded-full bg-red-100 p-2 mr-3">
            <ExclamationCircleOutlined className="text-red-600 text-xl" />
          </div>
          <span className="text-lg font-medium text-gray-700">Overdue Amount</span>
        </div>
        <CurrencyBreakdown type="overdueAmount" className="text-red-600" />
        <CurrencyPercentBreakdown type="overdueAmount" />
      </Card>
    </div>
  );
};

export default CardsContainer;
